import { TreeData } from '@/common/selects/CommonTreeSelect';

export const sortByExactMatch = <
  T extends Record<string, unknown>,
  K extends keyof T,
>(
  items: T[],
  searchTerm: string,
  labelKey: K,
): T[] => {
  if (!searchTerm) return items;
  const lower = searchTerm.toLowerCase();
  return [...items].sort((a, b) => {
    const aValue = a[labelKey];
    const bValue = b[labelKey];
    const aExact =
      (typeof aValue === 'string' ? aValue : '').toLowerCase() === lower
        ? 0
        : 1;
    const bExact =
      (typeof bValue === 'string' ? bValue : '').toLowerCase() === lower
        ? 0
        : 1;
    return aExact - bExact;
  });
};

export const sortTreeDataByExactMatch = (
  treeData: TreeData[],
  searchTerm: string,
): TreeData[] => {
  if (!searchTerm) return treeData;
  const lower = searchTerm.toLowerCase();

  const matchCache = new Map<TreeData, boolean>();

  const hasExactMatch = (node: TreeData): boolean => {
    if (matchCache.has(node)) return matchCache.get(node)!;
    const titleStr =
      typeof node.title === 'string' ? node.title.toLowerCase() : '';
    const selfMatches = titleStr === lower;
    const childMatches = (node.children ?? []).some(hasExactMatch);
    const result = selfMatches || childMatches;
    matchCache.set(node, result);
    return result;
  };

  treeData.forEach(hasExactMatch);

  const sortNodes = (nodes: TreeData[]): TreeData[] => {
    const sorted = [...nodes].sort((a, b) => {
      const aExact = matchCache.get(a) ? 0 : 1;
      const bExact = matchCache.get(b) ? 0 : 1;
      return aExact - bExact;
    });
    return sorted.map((node) => {
      if (!node.children || node.children.length === 0) return node;
      return { ...node, children: sortNodes(node.children) };
    });
  };

  return sortNodes(treeData);
};

export const getSplittedSearchValues = (searchValue: string): string[] => {
  if (!searchValue) {
    return [];
  }
  return searchValue
    .trim()
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
};

export const getTokenizedMatchedValues = (
  searchValues: string[],
  availableValues: { [key: string]: string }[] | TreeData[],
  matchKey: string,
) => {
  const matchedValues = searchValues.filter((searchValue) => {
    return availableValues.some(
      (item) => item[matchKey].toLowerCase() === searchValue.toLowerCase(),
    );
  });
  return matchedValues;
};

export const getTokenizedUnmatchedValues = (
  searchValues: string[],
  availableValues: { [key: string]: string }[] | TreeData[],
  matchKey: string,
) => {
  const matchedValues = getTokenizedMatchedValues(
    searchValues,
    availableValues,
    matchKey,
  );
  const unmatchedValues = searchValues.filter((searchValue) => {
    return !matchedValues.includes(searchValue);
  });
  return unmatchedValues;
};
